
import TemplatesService from '@/services/TemplatesService'
import userInfoStore from '@/store/userInfo'
import { PermissionNames, UserDomain } from '@/types/Auth'
import { BasicEntity, CatalogGridItemColumn, CrudActions, Domain, DomainCatalogFilters, UserCatalogFilters } from '@/types/Catalogs'
import { AsyncTask, AsyncTaskFilters, AsyncTaskStatus, AsyncTaskType } from '@/types/catalogs/AsyncTask'
import { PageRequest, SortDirection } from '@/types/Support'
import { TemplateFilters } from '@/types/Templates'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import InputSelectorFilter from '@/components/generic/InputSelectorFilter.vue'
import ConfirmationModal from '@/components/generic/ConfirmationModal.vue'
import Vue from 'vue'
import CatalogsFillsService from '@/services/CatalogsFillsService'
import LoadingMask from '@/components/generic/LoadingMask.vue'
import CatalogsService from '@/services/CatalogsService'
import { Project, ProjectCatalogFilters } from '@/types/catalogs/Projects'
import AdminAsyncTaskForm from '@/components/admin/AdminAsyncTaskForm.vue'
import { CustomSearch } from '@/types/catalogs/CustomSearch'
import { CustomView } from '@/types/catalogs/Views'
import LogService, { ToString } from '@/services/support/LogService'
import { MessageError } from '@/components/generic/MessageHandling.vue'

export default Vue.extend({
  components: {
    TopToolBarPaginator,
    InputSelectorFilter,
    ConfirmationModal,
    AdminAsyncTaskForm,
    LoadingMask
  },
  data () {
    return {
      asyncTaskClass: AsyncTask,
      PAGE_SIZE: 500,
      PAGE_SIZE_TEMPLATES: 100,
      PAGE_SIZE_TASKS: 50,
      paginatorKey: 0,
      leftSidebarCollapsed: false,
      crudActions: CrudActions,
      currentAction: CrudActions.READ as CrudActions,
      filters: new AsyncTaskFilters(),
      lastFilters: new AsyncTaskFilters(),
      taskStatus: [
        { label: 'Nueva', value: AsyncTaskStatus.NEW },
        { label: 'En espera', value: AsyncTaskStatus.WAITING },
        { label: 'En proceso', value: AsyncTaskStatus.PROCESSING },
        { label: 'Terminada', value: AsyncTaskStatus.DONE },
        { label: 'Cancelada', value: AsyncTaskStatus.CANCELED },
        { label: 'Error', value: AsyncTaskStatus.ERROR }
      ],
      finalTaskStatuses: [
        AsyncTaskStatus.DONE,
        AsyncTaskStatus.ERROR,
        AsyncTaskStatus.CANCELED
      ],
      taskTypeSelection: [
        { label: 'Todos', value: AsyncTaskType.NONE },
        { label: 'Mi Tareas', value: AsyncTaskType.DATAFEED },
        { label: 'Integración', value: AsyncTaskType.INTEGRATION }
      ],
      templates: [] as BasicEntity[],
      users: [] as BasicEntity[],
      usersQueried: [] as BasicEntity[],
      loadingDomains: false,
      loadingProjects: false,
      loadingTemplates: false,
      loadingUsers: false,
      loadingTasks: false,
      taskItems: [] as AsyncTask[],
      totalTaskItems: 0,
      taskSelected: {} as AsyncTask,
      taskColumns: [
        CatalogGridItemColumn.def('id', 'ID'),
        CatalogGridItemColumn.def('name', 'Nombre'),
        CatalogGridItemColumn.def('type', 'Tipo'),
        CatalogGridItemColumn.def('status', 'Estado'),
        CatalogGridItemColumn.def('percentage', 'Porcentaje'),
        CatalogGridItemColumn.def('duration', 'Duración'),
        CatalogGridItemColumn.def('requestId', 'RequestID'),
        CatalogGridItemColumn.def('creationDate', 'Fecha de creación'),
        CatalogGridItemColumn.def('updateDate', 'Fecha de actualización')
      ],
      paginationIndex: 1,
      showModalFlag: false,
      modalId: 'confirmationToCancelModal',
      taskCanceled: false,
      taskCanceledSuccesfully: false,
      taskCanceledFailed: false,
      domainItems: [] as BasicEntity[],
      projectItems: [] as BasicEntity[],
      domainSelected: new BasicEntity(-1, ''),
      projectSelected: new BasicEntity(-1, ''),
      templateSelected: new BasicEntity(-1, ''),
      userSelected: new BasicEntity(-1, ''),
      loadedSearches: {} as any,
      loadedViews: {} as any,
      USER_NO_IDENTIFIED: 'No identificado' as string,
      tasksVerified: [] as AsyncTask[],
      showTasksVerifiedToast: false
    }
  },
  created () {
    this.addUserColumn()
    if (this.permissionToReadDomains) {
      this.loadDomains(this.preparePageDomains())
        .then(() => {
          this.updateDomainSelected(this.userDefaultDomain)
          this.loadAsyncTasks(true)
        })
    } else {
      this.loadAsyncTasks(true)
    }
  },
  computed: {
    userDefaultDomain () : UserDomain {
      return userInfoStore.userInfo.domain
    },
    /** @returns true if the user can read domains */
    permissionToReadDomains (): boolean {
      return userInfoStore.hasPermission(PermissionNames.DOMAINS_READ)
    },
    /** @returns true if the user can read projects */
    permissionToReadProjects (): boolean {
      return userInfoStore.hasPermission(PermissionNames.PROJECTS_READ)
    },
    /**
     * @returns true if the user has the required permissions to read tasks
     */
    permissionToReadTasks (): boolean {
      const requiredPermissions = [
        PermissionNames.TASKS_VIEW,
        PermissionNames.TASKS_READ
      ]
      return userInfoStore.hasPermissions(requiredPermissions)
    },
    /**
     * @returns true if the user has the required permissions to delete tasks
     */
    permissionToDeleteTasks (): boolean {
      const requiredPermissions = [
        PermissionNames.DATAFEED_DELETE,
        PermissionNames.INTEGRATION_DELETE
      ]
      return userInfoStore.hasPermissions(requiredPermissions)
    },
    /**
     * @returns true if the current user can read templates to use as filters
     */
    permissionToReadTemplates (): boolean {
      let requiredPermissions = [
        PermissionNames.TEMPLATES_READ,
        PermissionNames.PROJECTS_READ
      ]
      return userInfoStore.hasPermissions(requiredPermissions)
    },
    /**
     * @returns true if the user has permissions to read user records
     */
    permissionToReadUsers (): boolean {
      return userInfoStore.hasPermission(PermissionNames.USERS_READ)
    },
    /**
     * @returns true if the user has permissions to read custom searches
     */
    permissionToReadSearches (): boolean {
      return userInfoStore.hasPermission(PermissionNames.FILLS_READ)
    },
    /**
     * @returns true if the user has permissions to read views
     */
    permissionToReadViews (): boolean {
      let requiredPermissions = [
        PermissionNames.FILLS_READ,
        PermissionNames.PROJECTS_READ
      ]
      return userInfoStore.hasPermissions(requiredPermissions)
    },
    permissionToVerifiedTasks (): boolean {
      return userInfoStore.hasPermission(PermissionNames.TASKS_VERIFICATION)
    },
    /** @returns true if there is a task selected */
    anyTaskSelected (): boolean {
      return this.taskSelected.id > 0
    },
    /** @returns the modal id to work with in the modal */
    modalConfirmationId (): string {
      return `#${this.modalId}`
    },
    /** @returns the modal title */
    modalConfirmationTitle () {
      return 'Cancelación de tarea'
    }
  },
  methods: {
    addUserColumn (): void {
      const AVOID_COLUMNS_FROM_RIGHT = 2
      if (this.permissionToReadUsers) {
        let insertIntoIndex = this.taskColumns.length - AVOID_COLUMNS_FROM_RIGHT
        this.taskColumns.splice(insertIntoIndex, 0, CatalogGridItemColumn.def('user', 'Usuario'))
      }
    },
    /** Clear the filters that are use to search async tasks */
    clearFilters (): void {
      this.resetPaginator()
      this.filters = new AsyncTaskFilters()
      this.setDomainOnFilters(this.domainSelected)
      this.setProjectOnFilters(new BasicEntity(-1, ''))
      this.setTemplateOnFilters(new BasicEntity(-1, ''))
    },
    /**
     * Prepare a page request to get all domains
     *
     * @returns a PageRequest
     */
    preparePageDomains (): PageRequest<DomainCatalogFilters> {
      const pageDomains = new PageRequest<DomainCatalogFilters>()
      pageDomains.sortOrder = SortDirection.DESC
      pageDomains.sort = 'name'
      pageDomains.size = this.PAGE_SIZE
      return pageDomains
    },
    /**
     * Get all the domains from the API users
     *
     * @param page with the configurations to get the domains
     */
    async loadDomains (page: PageRequest<DomainCatalogFilters>): Promise<void> {
      this.loadingDomains = true
      CatalogsService.getDomains(page)
        .then((response) => {
          this.domainItems.push(
            ...response.results.map((domain: Domain) => new BasicEntity(domain.id, domain.name))
          )
          if (this.domainItems.length < response.total) {
            page.page++
            this.loadDomains(page)
          }
        })
        .finally(() => {
          this.loadingDomains = false
        })
    },
    /**
     * Prepare a page request to get all projects
     *
     * @param domainId from the currento domain selected
     * @returns a PageRequest
     */
    preparePageProjects (domainId: number): PageRequest<ProjectCatalogFilters> {
      const pageProjects = new PageRequest<ProjectCatalogFilters>()
      pageProjects.sortOrder = SortDirection.DESC
      pageProjects.sort = 'name'
      pageProjects.size = this.PAGE_SIZE
      if (domainId >= 0) pageProjects.filters.domain = domainId
      return pageProjects
    },
    /**
     * Get all the projects
     *
     * @param page with the configurations to get projects
     */
    async loadProjects (page: PageRequest<ProjectCatalogFilters>): Promise<void> {
      this.loadingProjects = true
      CatalogsService.getProjects(page)
        .then((response) => {
          this.projectItems.push(
            ...response.results.map((project: Project) => new BasicEntity(project.id, project.name))
          )
          if (this.projectItems.length < response.total) {
            page.page++
            this.loadProjects(page)
          }
        })
        .finally(() => {
          this.loadingProjects = false
        })
    },
    /**
     * Based on a domain id, load the projects that belong to it.
     *
     * @param domainId id of the domain
     */
    reloadProjects (domainId: number) {
      let projectConfig = this.preparePageProjects(domainId)
      this.projectItems = []
      this.updateProjectSelected(new BasicEntity(-1, ''))
      this.loadProjects(projectConfig)
    },
    /**
     * Based on a domain id and a project id, load the templates that belong to them.
     *
     * @param domainId id of the domain
     * @param projectId id of the project
     */
    reloadTemplates (domainId: number, projectId: number) {
      let templatesConfig = this.preparePageTemplates(domainId, projectId)
      this.templates = []
      this.setTemplateOnFilters(new BasicEntity(-1, ''))
      this.loadTemplates(templatesConfig)
    },
    setDomainOnFilters (domain: BasicEntity): void {
      this.domainSelected = domain
      this.filters.domain = domain.id
    },
    setProjectOnFilters (project: BasicEntity): void {
      this.projectSelected = project
      this.filters.project = project.id
    },
    /**
     * When a domain is selected, we reload the projects and the templates
     * to get what belongs to that domain.
     *
     * @param domain selected
     */
    updateDomainSelected (domain: BasicEntity) {
      this.setDomainOnFilters(domain)
      if (this.permissionToReadProjects) this.reloadProjects(domain.id)
      if (this.permissionToReadUsers) this.reloadUsers(domain.id)
    },
    /**
     * When a project is selected, we work with this values to update
     * the project filter value and reload the templates.
     *
     * @param project selected
     */
    updateProjectSelected (project: BasicEntity) {
      this.setProjectOnFilters(project)
      if (this.permissionToReadTemplates) this.reloadTemplates(this.domainSelected.id, project.id)
    },
    /**
     * When a template is selected, we update its value on filters
     *
     * @param template selected
     */
    setTemplateOnFilters (template: BasicEntity) {
      this.templateSelected = template
      this.filters.template = this.templateSelected.id
    },
    /**
     * When an user is selected, we update its value in the filters
     *
     * @param user selected
     */
    updateUserSelected (user: BasicEntity) {
      this.userSelected = user
      this.filters.user = this.userSelected.id
    },
    /**
     * Prepare a page request to query the tasks
     */
    preparePageTasks (): PageRequest<AsyncTaskFilters> {
      let pageRequest = new PageRequest<AsyncTaskFilters>()
      pageRequest.size = this.PAGE_SIZE_TASKS
      pageRequest.sort = 'id'
      pageRequest.page = this.paginationIndex - 1
      pageRequest.filters = this.filters
      return pageRequest
    },
    /**
     * Query the async tasks with a page configuration
     *
     * @param pageRequest with the filters to query
     */
    getAsyncTasks (pageRequest: PageRequest<AsyncTaskFilters>, updateTotalTasks: boolean = false)
    : void {
      this.loadingTasks = true
      CatalogsFillsService.getAsyncTasks(pageRequest)
        .then((response) => {
          if (updateTotalTasks) this.totalTaskItems = response.total
          this.taskItems.push(...response.results
            .map((task: AsyncTask) => {
              return this.taskIntance(task)
            })
          )
        })
        .finally(() => {
          this.loadingTasks = false
          this.setUserNameToTasksItems()
        })
    },
    /**
     * Set the user name for each task in the grid
     */
    async setUserNameToTasksItems () {
      if (this.permissionToReadUsers) {
        for (const task of this.taskItems) {
          await this.setUserNameToAsyncTask(task)
        }
      }
    },
    /**
     * Set the user name to one task
     * @param task to set user name
     */
    async setUserNameToAsyncTask (task: AsyncTask) {
      let user = this.getUserFromLocalUsers(task.user)
      if (user.id !== -1) {
        this.setUserNameTask(task, user.name)
      } else {
        await this.getUserFromAPI(task.user)
          .then(user => {
            this.addUserToQueriedUsers(user)
            this.setUserNameTask(task, user.name)
          })
          .catch(() => {
            this.setUserNameTask(task, this.USER_NO_IDENTIFIED)
          })
      }
    },
    /**
     * Get the user entity from the users in the current domain
     * @param userId to get
     */
    getUserFromLocalUsers (userId: number): BasicEntity {
      let user = this.users.find(user => user.id === userId)
      return user ?? new BasicEntity(-1, '')
    },
    /**
     * Set the user name into the task record
     * @param task to set user name
     * @param userName to set in the task
     */
    setUserNameTask (task: AsyncTask, userName: string): void {
      task.userName = userName
    },
    /**
     * Get the user entity from users previously queried or
     * make the request to the API
     * @param userId to query
     */
    async getUserFromAPI (userId: number): Promise<BasicEntity> {
      let user = this.getUserFromUsersQueried(userId)
      if (user.id !== -1) {
        return user
      }
      user = await CatalogsService.getUserById(userId)
      let userEntity = user.id ? new BasicEntity(user.id, user.name) : new BasicEntity(-1, '')
      return userEntity
    },
    /**
     * Get the user entity from the user queried from the API
     * @param userId to get
     */
    getUserFromUsersQueried (userId: number): BasicEntity {
      let user = this.usersQueried.find(user => user.id === userId)
      return user ?? new BasicEntity(-1, '')
    },
    /**
     * Add a user into the usersQueried list
     * @param user to add
     */
    addUserToQueriedUsers (user: BasicEntity): void {
      let userIndex = this.usersQueried.findIndex((u) => u.id === user.id)
      if (user.id > 0 && userIndex === -1) this.usersQueried.push(user)
    },
    /**
     * Get the task by Id 'taskId' and update its information into the 'taskItems'
     *
     * @param task to update
     */
    updateAsyncTask (task: AsyncTask) {
      const taskIndex = this.taskItems.findIndex(iTask => iTask.id === task.id)
      this.taskItems.splice(taskIndex, 1, task)
      this.selectTask(task)
    },
    /**
     * Localy save one search that had been requested into the detail view
     *
     * @param search to save
     */
    localSaveSearch (search: CustomSearch): void {
      this.loadedSearches[search.id] = search
    },
    /**
     * Localy save one view that had been requested into the detail view
     *
     * @param view to save
     */
    localSaveView (view: CustomView): void {
      this.loadedViews[view.id] = view
    },
    /**
     * Reset the paginator and applies the search
     */
    searchTasks (): void {
      this.resetPaginator()
      this.loadAsyncTasks(true)
    },
    /**
     * Load the async tasks
     */
    loadAsyncTasks (updateTotal: boolean = false): void {
      if (this.loadingTasks) return
      this.unselectTask()
      this.taskItems = []
      let pageRequest = this.preparePageTasks()
      this.getAsyncTasks(pageRequest, updateTotal)
    },
    /**
     * @returns a new AsyncTasnk instance based on a task
     */
    taskIntance (task: AsyncTask): AsyncTask {
      return AsyncTask.createInstance(task)
    },
    /**
     * Get a specific property from a task
     *
     * @param task from where get the property
     * @param property name to get its value
     */
    taskProperty (task: AsyncTask, property: string): any {
      return this.asyncTaskClass.getPropByName(task, property)
    },
    /**
     * Get the title to set in each row task into the 'td' in the table
     *
     * @param task from where get the property value
     * @param property to get
     */
    taskTitle (task: AsyncTask, property: string): string {
      let value = this.asyncTaskClass.getPropByName(task, property)
      return value && value === 'string' && value.length > 40 ? value : ''
    },
    /**
     * Prepare a page to request the templates for the filters
     *
     * @param domainId from the current domain selected
     * @param projectId from the current project selected
     * @returns a page with the configurations to requets templates
     */
    preparePageTemplates (domainId: number, projectId: number): PageRequest<TemplateFilters> {
      let pageRequest = new PageRequest<TemplateFilters>()
      pageRequest.size = this.PAGE_SIZE_TEMPLATES
      pageRequest.sort = 'name'
      if (domainId >= 0) pageRequest.filters.domain = domainId
      if (projectId > 0) pageRequest.filters.project = projectId
      return pageRequest
    },
    /**
     * Prepare a page to request get all the user records
     *
     * @param domainId from the current domain selected
     * @returns a page with the configurations to requets users
     */
    preparePageUsers (domainId: number): PageRequest<UserCatalogFilters> {
      let pageRequest = new PageRequest<UserCatalogFilters>()
      pageRequest.size = this.PAGE_SIZE
      pageRequest.sort = 'name'
      if (domainId >= 0) pageRequest.filters.domainId = domainId
      return pageRequest
    },
    /**
     * Query the async tasks to display in the table
     *
     * @param page with the configurations to query the async tasks
     */
    loadTemplates (page: PageRequest<TemplateFilters>): void {
      this.loadingTemplates = true
      TemplatesService.getAllTemplates(page)
        .then((response) => {
          this.templates.push(
            ...response.results.map(t => new BasicEntity(t.id, `${t.label} [v${t.version}]`))
          )
          if (this.templates.length < response.total) {
            page.page++
            this.loadTemplates(page)
          }
        })
        .finally(() => {
          this.loadingTemplates = false
        })
    },
    /**
     * Query the users to display as options in the filters
     *
     * @param page with the configurations to query the user records
     */
    loadUsers (page: PageRequest<UserCatalogFilters>): void {
      this.loadingUsers = true
      CatalogsService.getUsers(page)
        .then((response) => {
          this.users.push(
            ...response.results.map(user => new BasicEntity(user.id, user.name))
          )
          if (this.users.length < response.total) {
            page.page++
            this.loadUsers(page)
          }
        })
        .finally(() => {
          this.loadingUsers = false
        })
    },
    /**
     * Clear the current users to get the current users
     * from a certain domain
     *
     * @param domainId id to the current domain to get users
     */
    reloadUsers (domainId: number): void {
      this.users = []
      this.updateUserSelected(new BasicEntity(-1, ''))
      this.loadUsers(this.preparePageUsers(domainId))
    },
    /**
     * Delete (cancel) a task selected
     */
    cancelTask () {
      CatalogsFillsService.deleteTask(this.taskSelected.id)
        .then((response) => {
          this.taskCanceledSuccesfully = true
          this.updateTask(response)
        })
        .catch((error) => {
          this.taskCanceledFailed = true
          LogService.error(`Error al cancelar la descarga: ${ToString(error.response ?? error)}`)
        })
        .finally(() => {
          this.taskCanceled = true
          setTimeout(() => {
            this.hideTaskToast()
          }, 8000)
        })
    },
    /** Display the modal for cancel a task */
    showCancelTaskModal () {
      this.showModalFlag = true
      setTimeout(() => {
        const modal = document.getElementById(this.modalId)
        modal!.style.display = 'block'
      }, 100)
    },
    /** Hide the modal for cancel a task */
    hideTaskToast () {
      this.showModalFlag = false
      this.taskCanceled = false
      this.taskCanceledSuccesfully = false
      this.taskCanceledFailed = false
    },
    /**
     * Updates a task element into the current list of tasks
     *
     * @param task with the new values to update
     */
    updateTask (task: AsyncTask): void {
      let index = this.taskItems.findIndex(t => t.id === task.id)
      let newTask = this.taskIntance(task)
      this.selectTask(newTask)
      this.taskItems.splice(index, 1, newTask)
    },
    /**
     * Set the 'taskSelected' with the task that receive as param
     *
     * @param task to set as selected task
     */
    selectTask (task: AsyncTask): void {
      switch (this.currentAction) {
        case this.crudActions.READ:
        case this.crudActions.READ_DETAIL:
          this.taskSelected = task
          break
      }
    },
    /**
     * Clear the selected task
     */
    unselectTask (): void {
      this.taskSelected = new AsyncTask()
    },
    /**
     * Compare the last task selected with one that reveice as param
     * and validate if both are the same based on its id
     *
     * @param task to compare with
     */
    isTaskSelected (task: AsyncTask): boolean {
      return this.taskSelected.id === task.id
    },
    /**
     * If the task that received as param has an id valid (grather than cero)
     * set the current actions as READ_DETAIL to open the view form
     *
     * @param task to validate
     */
    openTaskForm (): void {
      if (this.anyTaskSelected) {
        this.currentAction = this.crudActions.READ_DETAIL
      }
    },
    /**
     * Set the current action as READ, to view all the tasks
     */
    closeTaskForm (): void {
      this.currentAction = this.crudActions.READ
    },
    /**
     * @param task to set as selected and open its view form
     */
    selectAndOpenTask (task: AsyncTask): void {
      this.selectTask(task)
      this.openTaskForm()
    },
    /**
     * Manage the change pagination
     *
     * @param page index of the current page selected
     */
    changePagination (page: number): void {
      this.paginationIndex = page
      this.loadAsyncTasks()
    },
    /**
     * Return the page index to the first and create a new key for the paginator
     */
    resetPaginator () {
      this.paginatorKey = new Date().getTime()
      this.paginationIndex = 1
    },
    verifiedAsyncTasks () {
      CatalogsFillsService.taskVerification()
        .then((response) => {
          this.tasksVerified = response.results
          this.activeTasksVerifiedToast()
        })
        .catch((error) => {
          const messageError = {
            title: 'Error al verificar las tareas.',
            message: '',
            delay: 10000,
            error: error
          }
          new MessageError(messageError).displayMessage()
        })
        .finally(() => {
          this.autoHideTasksVerifiedToast()
        })
    },
    hideTasksVerifiedToast () {
      this.tasksVerified = []
      this.showTasksVerifiedToast = false
    },
    activeTasksVerifiedToast () {
      this.showTasksVerifiedToast = true
    },
    autoHideTasksVerifiedToast () {
      setTimeout(() => {
        this.hideTasksVerifiedToast()
      }, 10000)
    }
  }
})
